import { getUserExists } from "@/services/api/auth";
import { getCorporateExistPhoneNumber } from "@/services/api/corporate";
import {
  ImageAcceptType,
  InquiryContactType,
} from "@/services/constants/global";
import i18n from "@/services/translations/i18n";
import { formatDate } from "@/services/utils/formatter";
import { getFileType } from "@/services/utils/general";
import { isMatch } from "date-fns";
import validator from "validator";
import * as Yup from "yup";

const ERROR_MESSAGE = {
  REQUIRED: i18n.t("This data is required."),
  TEXT_ONLY: i18n.t("Invalid information. Please input text and space only."),
  TEXT_AND_NUMBER_ONLY: i18n.t(
    "Invalid information. Please input text and number only."
  ),
  INVALID_INFO: i18n.t("Invalid information."),
  INVALID_URL: i18n.t("Invalid URL."),
};

Yup.setLocale({
  mixed: {
    required: ERROR_MESSAGE.REQUIRED,
  },
  string: {
    url: ERROR_MESSAGE.INVALID_URL,
  },
});

export const MAX_FILE_SIZE = 3; //MB
export const MAX_LENGTH_INPUT = 100;
export const MAX_LENGTH_PHONE_NUMBER = 13;
export const MAX_LENGTH_LANDLINE_NUMBER = 14;
export const MAX_LENGTH_NAME = 50;
export const MAX_LENGTH_RECRUITMENT_POSITION = 50;
export const MAX_LENGTH_MONTH_SALARY = 20;
export const MAX_PERSONNEL = 8;
export const MAX_LENGTH_WORKING_DAY = 30;
export const MAX_LENGTH_WORKING_HOURS = 30;
export const MAX_LENGTH_ADMISSIONS_PROCEDURE = 30;
export const MAX_LENGTH_TEXTAREA = 1000;
export const DEFAULT_DATE_OF_BIRTH = formatDate(
  new Date(new Date().setFullYear(new Date().getFullYear() - 18))
);

export const CORPORATE_PHOTOS_MAX_COUNT = 10;
export const CORPORATE_LOGOS_MAX_COUNT = 10;

/*
 *Regex
 */
export const SPECIAL_CHARACTER_REGEX = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/g;
export const KOREAN_PHONE_REGEX = /^01[0-9]{1}-[0-9]{4}-[0-9]{4}$/;
export const KOREAN_LANDLINE_REGEX = /^[0-9-]+$/;

export const PASSWORD_REGEX = /^(?=.*[0-9])(?=.*[a-zA-Z]).{4,}$/;

export const FULL_NAME_REGEX =
  /^[a-zA-Z\s\u3131-\u314e\u314f-\u3163\uac00-\ud7a3]+$/;

export const TEXT_ONLY_REGEX =
  /^[a-zA-Z\s\u3131-\u314e\u314f-\u3163\uac00-\ud7a3]+$/;

export const TEXT_AND_NUMBER_ONLY =
  /^[A-Za-z0-9\s\u3131-\u314e\u314f-\u3163\uac00-\ud7a3]+$/;
export const ADDRESS_REGEX =
  /^[a-zA-Z0-9`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~\s\u3131-\u314e\u314f-\u3163\uac00-\ud7a3]+$/;
export const ISO_DATE_REGEX =
  /^([+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([.,]\d+(?!:))?)?(\17[0-5]\d([.,]\d+)?)?([zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;

/*
 *Yup validation schema
 */

export const requiredStringSchema = Yup.string().trim().required();
export const requiredDateSchema = Yup.string()
  .trim()
  .test(
    "date-format",
    i18n.t("Incorrect date format."),
    (value) => isMatch(value, "yyyy-MM-dd") || ISO_DATE_REGEX.test(value)
  )
  .required();
export const requiredStringTextOnlySchema = Yup.string()
  .trim()
  .required()
  .matches(TEXT_ONLY_REGEX, ERROR_MESSAGE.TEXT_ONLY);
export const requiredStringTextOrNumberOnlySchema = Yup.string()
  .trim()
  .required()
  .matches(TEXT_AND_NUMBER_ONLY, ERROR_MESSAGE.TEXT_AND_NUMBER_ONLY);
export const requiredNumberSchema = Yup.number(
  i18n.t("This data must be a number")
).required();

export const username = Yup.string()
  .trim()
  .required()
  .matches(TEXT_AND_NUMBER_ONLY, ERROR_MESSAGE.TEXT_AND_NUMBER_ONLY)
  .test(
    "space",
    ERROR_MESSAGE.TEXT_AND_NUMBER_ONLY,
    (value) => !value.includes(" ")
  );

export const fullName = Yup.string()
  .trim()
  .required()
  .matches(FULL_NAME_REGEX, ERROR_MESSAGE.TEXT_ONLY);

export const email = Yup.string()
  .trim()
  .required()
  .email(i18n.t("Please enter a valid email address."))
  .test("email", i18n.t("Please enter a valid email address."), (email) =>
    validator.isEmail(email)
  );

export const password = Yup.string()
  .trim()
  .required()
  .matches(
    PASSWORD_REGEX,
    i18n.t(
      "Currently, an error message is displayed when a special character is included. Please correct this issue."
    )
  );

export const passwordConfirm = Yup.string()
  .trim()
  .required()
  .oneOf([Yup.ref("password")], i18n.t("Password does not match."));

export const phone = Yup.string()
  .trim()
  .required(i18n.t("The mobile phone number has not been entered."))
  .matches(
    KOREAN_PHONE_REGEX,
    i18n.t("Please enter a valid Korean phone number.")
  )
  .test(
    "check-phone-exists",
    i18n.t("Phone number already registered."),
    async (value) => {
      if (!value) return true;
      try {
        const exists = await getUserExists(null, value);
        return !exists;
      } catch (error) {
        console.error("Error checking phone existence:", error);
        return false;
      }
    }
  );

export const corporatePhone = Yup.string()
  .trim()
  .required(i18n.t("The mobile phone number has not been entered."))
  .matches(
    KOREAN_PHONE_REGEX,
    i18n.t("Please enter a valid Korean phone number.")
  )
  .test(
    "check-phone-exists",
    i18n.t("Phone number already registered."),
    async function (value) {
      if (!value) return true;
      console.log(this.options);
      console.log(this.parent);
      const id = this.parent._id || null;

      console.log("Validation ID:", id);

      try {
        const exists = await getCorporateExistPhoneNumber(id, value);
        return !exists;
      } catch (error) {
        console.error("Error checking phone existence:", error);
        return false;
      }
    }
  );

export const landline = Yup.string()
  .trim()
  // .required()
  .matches(
    KOREAN_LANDLINE_REGEX,
    i18n.t("Please enter a valid Korean landline number.")
  )
  .min(11, i18n.t("Please enter a valid Korean landline number."))
  .max(16, i18n.t("Please enter a valid Korean landline number."));

export const termAgreed = Yup.bool().oneOf([true]);

export const infoCollectionAndUsageAgreed = Yup.bool().oneOf([true]);

export const companyType = Yup.string().required();

export const companyName = Yup.string()
  .trim()
  .required()
  .matches(TEXT_AND_NUMBER_ONLY, ERROR_MESSAGE.TEXT_AND_NUMBER_ONLY);

export const registrationNumber = Yup.string()
  .trim()
  .required()
  .matches(TEXT_AND_NUMBER_ONLY, ERROR_MESSAGE.TEXT_AND_NUMBER_ONLY);

export const representative = Yup.string()
  .trim()
  .required()
  .matches(TEXT_ONLY_REGEX, ERROR_MESSAGE.TEXT_ONLY);
export const homePageUrl = Yup.string().trim().url();
export const personnel = Yup.number()
  .typeError(i18n.t("Must be a number."))
  .required()
  .max(8, i18n.t("Please enter up to 8 persons."));

export const title = Yup.string().trim().required();
export const url = Yup.string().trim().required().url();
export const description = Yup.string().trim();

export const address = Yup.string()
  .trim()
  .required()
  .matches(ADDRESS_REGEX, ERROR_MESSAGE.INVALID_INFO);

export const postcodeAddress = Yup.string().trim().required();

export const files = Yup.array()
  .min(1, ERROR_MESSAGE.REQUIRED)
  .test(
    "total-files-size",
    i18n.t("Max file size is {{maxFileSize}} MB.", {
      maxFileSize: MAX_FILE_SIZE,
    }),
    (files) => {
      let totalFileSize = 0;
      files.forEach((file) => {
        totalFileSize += file.size;
      });
      totalFileSize = totalFileSize / 1024 / 1024;
      return totalFileSize < MAX_FILE_SIZE;
    }
  );

export const attachments = Yup.array().test(
  "total-files-size",
  i18n.t("Max file size is {{maxFileSize}} MB.", {
    maxFileSize: MAX_FILE_SIZE,
  }),
  (files) => {
    if (!files || files?.length === 0) return true;
    let totalFileSize = 0;
    files.forEach((file) => {
      totalFileSize += file.size;
    });
    totalFileSize = totalFileSize / 1024 / 1024;
    return totalFileSize < MAX_FILE_SIZE;
  }
);

export const photos = Yup.array()
  .min(1, ERROR_MESSAGE.REQUIRED)
  .test(
    "total-files-size",
    i18n.t("Max file size is {{maxFileSize}} MB.", {
      maxFileSize: MAX_FILE_SIZE,
    }),
    (files) => {
      let totalFileSize = 0;
      files.forEach((file) => {
        totalFileSize += file.size;
      });
      totalFileSize = totalFileSize / 1024 / 1024;
      return totalFileSize < MAX_FILE_SIZE;
    }
  );

export const workExperiences = Yup.array().test(
  "work-experiences",
  i18n.t("Please fill all required field or remove invalid item."),

  (workExperiences) => {
    if (!workExperiences || workExperiences?.length === 0) return true;
    return !workExperiences.some((workExperience) => {
      return Object.values(workExperience).some((value) => !value);
    });
  }
);

export const imageSchema = Yup.mixed()
  .test(
    "fileType",
    i18n.t("Please upload file with png/ jpg/ jpeg format."),
    (value) => {
      if (!value) return true;
      const fileType = value.type || getFileType(value.originalName);
      return ImageAcceptType.split(", ").includes(fileType);
    }
  )
  .test(
    "fileSize",
    i18n.t("Please upload file not larger than {{size}}Mb.", {
      size: MAX_FILE_SIZE,
    }),
    (value) => {
      if (!value) return true;
      return value.size < MAX_FILE_SIZE * 1024 * 1024;
    }
  );

export const inquiryEmail = Yup.string()
  .trim()
  .when("typeContact", {
    is: (typeContact) => typeContact !== InquiryContactType.PHONE_CALL,
    then: (schema) => schema.required(),
  })
  .email(i18n.t("Please enter a valid email address."))
  .test(
    "email",
    i18n.t("Please enter a valid email address."),
    (email) => !email || validator.isEmail(email)
  );

export const inquiryPhone = Yup.string()
  .trim()
  .when("typeContact", {
    is: (typeContact) => typeContact !== InquiryContactType.EMAIL,
    then: (schema) => schema.required(),
  })
  .matches(
    KOREAN_PHONE_REGEX,
    i18n.t("Please enter a valid Korean phone number.")
  );

export const questionAndAnswerSchema = Yup.array().of(
  Yup.object().shape({
    question: requiredStringSchema,
    answer: requiredStringSchema,
  })
);

export const recruitmentPositionSchema = Yup.array()
  .of(
    Yup.object().shape({
      duty: requiredStringSchema,
      career: requiredStringSchema,
      salary: requiredStringSchema,
      personnel: personnel,
    })
  )
  .min(1, i18n.t("This data is required."));

export const recruitmentSubwaySchema = Yup.array()
  .of(
    Yup.object().shape({
      city: requiredStringSchema,
      line: requiredStringSchema,
      station: requiredStringSchema,
      note: Yup.string().trim(),
    })
  )
  .min(1, i18n.t("This data is required."));

export const workTypeSchema = Yup.array().min(
  1,
  i18n.t("This data is required.")
);

export const requiredPriceSchema = Yup.string()
  .trim()
  .required(i18n.t("Select the number of days for advertising application"));

export default Yup;
